import {
  Badge,
  Button,
  FormControlLabel,
  makeStyles,
  Paper,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Menu,
  MenuItem,
} from '@material-ui/core';
import {
  AddCircleOutline,
  CheckCircle,
  DescriptionOutlined,
  HighlightOffRounded,
  HourglassEmptyRounded,
  MoveToInbox,
  Search,
} from '@material-ui/icons';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { Can } from '../Can';
import NewTicketModal from '../NewTicketModal';
import TabPanel from '../TabPanel';
import TicketsList from '../TicketsList';
import TicketsQueueSelect from '../TicketsQueueSelect';
import toastError from '../../errors/toastError';
import useTickets from '../../hooks/useTickets';
import api from '../../services/api';
import { toast } from 'react-toastify';
import ConfirmationModal from '../ConfirmationModal';

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  tabsHeader: {
    flex: 'none',
    backgroundColor: theme.palette.background.default,
  },
  settingsIcon: {
    alignSelf: 'center',
    marginLeft: 'auto',
    padding: 8,
  },
  tab: {
    minWidth: 120,
    width: 120,
  },
  ticketOptionsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
  },
  serachInputWrapper: {
    flex: 1,
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },
  searchIcon: {
    color: theme.palette.primary.main,
    marginLeft: 6,
    marginRight: 6,
    alignSelf: 'center',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    borderRadius: 25,
    padding: '10px',
    outline: 'none',
  },
  badge: {
    right: 0,
  },
  show: {
    display: 'block',
  },
  hide: {
    display: 'none !important',
  },
  searchContainer: {
    display: 'flex',
    padding: '10px',
    borderBottom: '2px solid rgba(0, 0, 0, .12)',
  },
  reportFilters: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    flexShrink: 0,
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      width: '100%',
    },
  },
  dateInput: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: '6px 8px',
    fontSize: 12,
    minWidth: 138,
    backgroundColor: theme.palette.background.default,
  },
  selectFilter: {
    minWidth: 120,
    margin: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.dark,
      },
    },
    '& .MuiSelect-select': {
      padding: theme.spacing(1),
    },
  },
}));

const TicketsManager = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [searchParam, setSearchParam] = useState('');
  const [tab, setTab] = useState('open');
  const [tabOpen] = useState('open');
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [closeAction, setCloseAction] = useState(null);
  const { user } = useContext(AuthContext);
  const [, setOpenCount] = useState(0);
  const normalizedProfile = (user?.profile || '').toLowerCase();
  const isAdmin = normalizedProfile === 'admin' || normalizedProfile === 'masteradmin';
  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(isAdmin ? [] : userQueueIds || []);

  const { socket } = useSocket();

  const ticketsInAttendance = useTickets({
    status: 'open',
    showAll: showAllTickets,
    withUnreadMessages: 'false',
    queueIds: JSON.stringify(userQueueIds),
    speakAttendant: true
  });

  const ticketsWaiting = useTickets({
    status: 'pending',
    showAll: 'true',
    withUnreadMessages: 'false',
    queueIds: JSON.stringify(userQueueIds),
    speakAttendant: true
  });

  const ticketsClosed = useTickets({
    status: 'closed',
    showAll: 'true',
    withUnreadMessages: 'false',
    queueIds: JSON.stringify(userQueueIds),
    speakAttendant: true
  });
  const refetchInAttendance = ticketsInAttendance.refetch;
  const refetchWaiting = ticketsWaiting.refetch;
  const refetchClosed = ticketsClosed.refetch;

  useEffect(() => {
    if (isAdmin) {
      setShowAllTickets(true);
      setSelectedQueueIds([]);
    }
  }, [isAdmin]);

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    setSearchParam(searchedTerm);
    setTab(searchedTerm === '' ? 'open' : 'search');
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const applyPanelStyle = (status) => (tabOpen !== status ? { width: 0, height: 0 } : {});

  const handleCloseTickets = async (status) => {
    try {
      let statusToClose = status;
      if (status === 'pending') {
        statusToClose = 'pending';
      } else if (status === 'open') {
        statusToClose = 'open';
      } else if (status === 'all') {
        statusToClose = 'all';
      }

      let ticketsToClose = [];

      if (statusToClose === 'open') {
        ticketsToClose = ticketsInAttendance.tickets || [];
      } else if (statusToClose === 'pending') {
        ticketsToClose = ticketsWaiting.tickets || [];
      } else if (statusToClose === 'all') {
        ticketsToClose = [
          ...(ticketsInAttendance.tickets || []),
          ...(ticketsWaiting.tickets || [])
        ];
      }

      const phonesToReset = ticketsToClose
        .filter(ticket => ticket.contact && ticket.contact.number)
        .map(ticket => ticket.contact.number);

      const { data } = await api.put(`/tickets/close-all?status=${statusToClose}`);

      setAnchorEl(null);

      if (phonesToReset.length > 0 && socket && socket.connected) {
        socket.emit('resetMultipleConversations', {
          phones: phonesToReset,
          closeStatus: statusToClose,
          ticketsCount: ticketsToClose.length
        });

        socket.once('multipleConversationsReset', (resetData) => {
          if (resetData.success) {
            // Conversas resetadas com sucesso
          } else {
            console.warn('TicketsManager - Erro ao resetar conversas:', resetData.error);
          }
        });

        setTimeout(() => {
          console.log('TicketsManager - Timeout - Assumindo reset das conversas');
        }, 5000);
      } else {
        console.warn('TicketsManager - Socket não disponível para resetar conversas:', {
          hasSocket: !!socket,
          isConnected: socket?.connected,
          phonesCount: phonesToReset.length
        });
      }

      if (status === 'open') {
        setTab((tab) => (tab === 'open' ? 'pending' : 'open'));
        setTimeout(() => setTab('open'), 100);
      } else if (status === 'pending') {
        setTab((tab) => (tab === 'pending' ? 'open' : 'pending'));
        setTimeout(() => setTab('pending'), 100);
      } else if (status === 'all') {
        setTab((tab) => (tab === 'open' ? 'pending' : 'open'));
        setTimeout(() => setTab('open'), 100);
      }

      toast.success(
        t('tickets.notifications.closed.success') +
        (data.count
          ? ` (${data.count} ${t('tickets.notifications.closed.tickets')})`
          : '') +
        (phonesToReset.length > 0 ? ` - ${phonesToReset.length} ${t('conversas resetadas')}` : '')
      );

    } catch (err) {
      console.error('TicketsManager - Erro ao fechar tickets:', err);
      toast.error(
        err.response?.data?.error || t('tickets.notifications.closed.error')
      );
    }
  };

  const handleOpenConfirmation = (status) => {
    setCloseAction(status);
    setConfirmationOpen(true);
    setAnchorEl(null);
  };


  useEffect(() => {
    if (!socket) return;

    const handleTicketUpdate = (data) => {
      if (data.action === 'update' || data.action === 'delete') {
        refetchInAttendance();
        refetchWaiting();
        refetchClosed();
      }
    };

    socket.on('ticket', handleTicketUpdate);

    const handleReconnect = () => {
      refetchInAttendance();
      refetchWaiting();
      refetchClosed();
    };

    socket.on('connect', handleReconnect);
    socket.on('reconnect', handleReconnect);

    return () => {
      socket.off('ticket', handleTicketUpdate);
      socket.off('connect', handleReconnect);
      socket.off('reconnect', handleReconnect);
    };
  }, [socket, refetchInAttendance, refetchWaiting, refetchClosed]);


  const handleConfirmClose = async () => {
    await handleCloseTickets(closeAction);
    setConfirmationOpen(false);
    setCloseAction(null);
    console.log('Ação de fechamento confirmada para:', closeAction);

    console.log(ticketsInAttendance.tickets.length, ticketsWaiting.tickets.length, ticketsClosed.tickets.length);

    if (closeAction === 'open') {
      ticketsInAttendance.refetch();
      ticketsClosed.refetch();
    } else if (closeAction === 'pending') {
      ticketsWaiting.refetch();
      ticketsClosed.refetch();
    } else {
      ticketsInAttendance.refetch();
      ticketsWaiting.refetch();
      ticketsClosed.refetch();
    }
  };
  const handleExportAttendanceReport = async () => {
    try {
      if (reportStartDate && reportEndDate && reportStartDate > reportEndDate) {
        toast.error('A data inicial não pode ser maior que a data final.');
        return;
      }

      setExportingReport(true);

      const { data } = await api.get('/tickets', {
        params: {
          status: 'all',
          showAll: 'true',
          all: 'true',
          startDate: reportStartDate || undefined,
          endDate: reportEndDate || undefined,
        },
      });

      const tickets = data?.tickets || [];

      if (!tickets.length) {
        toast.info('Nenhum atendimento encontrado para gerar relatório.');
        return;
      }

      const normalizeStatusLabel = (status) => {
        if (status === 'open') return 'Aberto';
        if (status === 'pending') return 'Aguardando';
        if (status === 'closed') return 'Fechado';
        return status || '';
      };

      const formatDateTime = (dateValue) => {
        if (!dateValue) return '-';
        return new Date(dateValue).toLocaleString('pt-BR');
      };

      const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        const [year, month, day] = dateValue.split('-');
        return `${day}/${month}/${year}`;
      };

      const escapeHtml = (value) =>
        String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');

      const totalOpen = tickets.filter((ticket) => ticket.status === 'open').length;
      const totalPending = tickets.filter((ticket) => ticket.status === 'pending').length;
      const totalClosed = tickets.filter((ticket) => ticket.status === 'closed').length;
      const periodLabel = reportStartDate && reportEndDate
        ? `${formatDate(reportStartDate)} a ${formatDate(reportEndDate)}`
        : 'Período completo';
      const generatedAt = new Date().toLocaleString('pt-BR');
      const logoUrl = `${window.location.origin}/assets/logo_empresa.png`;

      const rowsHtml = tickets
        .map(
          (ticket) => `
            <tr>
              <td>${escapeHtml(ticket.id)}</td>
              <td>${escapeHtml(ticket.protocol || '-')}</td>
              <td>${escapeHtml(normalizeStatusLabel(ticket.status))}</td>
              <td>${escapeHtml(ticket.contact?.name || '-')}</td>
              <td>${escapeHtml(ticket.contact?.number || '-')}</td>
              <td>${escapeHtml(ticket.queue?.name || '-')}</td>
              <td>${escapeHtml(ticket.user?.name || '-')}</td>
              <td>${escapeHtml(formatDateTime(ticket.createdAt))}</td>
              <td>${escapeHtml(formatDateTime(ticket.updatedAt))}</td>
            </tr>`
        )
        .join('');

      const reportHtml = `
        <!doctype html>
        <html lang="pt-BR">
          <head>
            <meta charset="utf-8" />
            <title>Relatório de Atendimentos</title>
            <style>
              @page { size: A4 landscape; margin: 16mm 10mm; }
              body { font-family: Arial, sans-serif; color: #1f2937; margin: 0; }
              .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #a61b1b; padding-bottom: 12px; margin-bottom: 14px; }
              .brand { display: flex; align-items: center; gap: 12px; }
              .brand img { height: 44px; width: auto; }
              .brand h1 { font-size: 18px; margin: 0; color: #111827; }
              .meta { text-align: right; font-size: 12px; color: #374151; }
              .meta p { margin: 2px 0; }
              .cards { display: flex; gap: 10px; margin: 12px 0 14px; }
              .card { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; background: #f9fafb; }
              .card .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
              .card .value { font-size: 24px; font-weight: 700; color: #111827; margin-top: 4px; }
              table { width: 100%; border-collapse: collapse; font-size: 11px; }
              thead th { background: #a61b1b; color: #ffffff; text-align: left; padding: 8px 6px; border: 1px solid #991b1b; }
              tbody td { padding: 6px; border: 1px solid #e5e7eb; vertical-align: top; }
              tbody tr:nth-child(even) { background: #f9fafb; }
              .footer { margin-top: 10px; font-size: 10px; color: #6b7280; text-align: right; }
            </style>
          </head>
          <body>
            <header class="header">
              <div class="brand">
                <img src="${logoUrl}" alt="Digitar Informática" />
                <h1>Relatório de Atendimentos</h1>
              </div>
              <div class="meta">
                <p><strong>Período:</strong> ${escapeHtml(periodLabel)}</p>
                <p><strong>Gerado em:</strong> ${escapeHtml(generatedAt)}</p>
              </div>
            </header>

            <section class="cards">
              <article class="card">
                <div class="label">Total de Tickets</div>
                <div class="value">${tickets.length}</div>
              </article>
              <article class="card">
                <div class="label">Abertos</div>
                <div class="value">${totalOpen}</div>
              </article>
              <article class="card">
                <div class="label">Aguardando</div>
                <div class="value">${totalPending}</div>
              </article>
              <article class="card">
                <div class="label">Fechados</div>
                <div class="value">${totalClosed}</div>
              </article>
            </section>

            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Protocolo</th>
                  <th>Status</th>
                  <th>Contato</th>
                  <th>Telefone</th>
                  <th>Setor</th>
                  <th>Atendente</th>
                  <th>Criado em</th>
                  <th>Atualizado em</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
            <p class="footer">Digitar Informática • Relatório operacional</p>
          </body>
        </html>
      `;

      const reportWindow = window.open('', '_blank');
      if (!reportWindow) {
        toast.error('Não foi possível abrir a janela de relatório. Verifique o bloqueador de pop-up.');
        return;
      }

      reportWindow.document.open();
      reportWindow.document.write(reportHtml);
      reportWindow.document.close();
      reportWindow.focus();

      setTimeout(() => {
        reportWindow.print();
      }, 350);

      toast.success('Relatório em PDF preparado. Na impressão, escolha "Salvar como PDF".');
    } catch (err) {
      toastError(err);
    } finally {
      setExportingReport(false);
    }
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />
      <ConfirmationModal
        title={
          closeAction === 'all'
            ? t('ticketsManager.confirmationModal.closeAllTitle')
            : closeAction === 'open'
              ? t('ticketsManager.confirmationModal.closeOpenTitle')
              : t('ticketsManager.confirmationModal.closePendingTitle')
        }
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmClose}
      >
        {closeAction === 'all'
          ? t('ticketsManager.confirmationModal.closeAllMessage')
          : closeAction === 'open'
            ? t('ticketsManager.confirmationModal.closeOpenMessage')
            : t('ticketsManager.confirmationModal.closePendingMessage')}
      </ConfirmationModal>

      <Paper elevation={0} square className={classes.searchContainer}>
        <Search className={classes.searchIcon} />
        <input
          type="text"
          placeholder={t('tickets.search.placeholder')}
          className={classes.searchInput}
          value={searchParam}
          onChange={handleSearch}
        />
      </Paper>

      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value="open"
            icon={
              <Badge
                className={classes.badge}
                badgeContent={ticketsInAttendance.count || 0}
                overlap="rectangular"
                max={9999}
                color="secondary"
              >
                <Tooltip title={t('tickets.tabs.open.title')} placement="top" arrow>
                  <MoveToInbox />
                </Tooltip>
              </Badge>
            }
            classes={{ root: classes.tab }}
          />
          <Tab
            value="pending"
            icon={
              <Badge
                className={classes.badge}
                badgeContent={ticketsWaiting.count || 0}
                overlap="rectangular"
                max={9999}
                color="secondary"
              >
                <Tooltip
                  title={t('tickets.tabs.pending.title')}
                  placement="top"
                  arrow
                >
                  <HourglassEmptyRounded />
                </Tooltip>
              </Badge>
            }
            classes={{ root: classes.tab }}
          />
          <Tab
            value="closed"
            icon={
              <Badge
                className={classes.badge}
                badgeContent={ticketsClosed.count || 0}
                overlap="rectangular"
                max={9999}
                color="secondary"
              >
                <Tooltip
                  title={t('tickets.tabs.closed.title')}
                  placement="top"
                  arrow
                >
                  <CheckCircle />
                </Tooltip>
              </Badge>
            }
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>

      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        <Tooltip title={t('ticketsManager.buttons.newTicket')} placement="top" arrow>
          <Button variant="outlined" color="primary" onClick={() => setNewTicketModalOpen(true)}>
            <AddCircleOutline />
          </Button>
        </Tooltip>
        <Tooltip title={t('ticketsManager.buttons.closed')} placement="top" arrow>
          <Button variant="outlined" color="primary" onClick={(e) => setAnchorEl(e.currentTarget)}>
            <HighlightOffRounded />
          </Button>
        </Tooltip>
        <Tooltip title="Relatório de atendimentos (PDF)" placement="top" arrow>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExportAttendanceReport}
            disabled={exportingReport}
          >
            <DescriptionOutlined />
          </Button>
        </Tooltip>
        <div className={classes.reportFilters}>
          <input
            type="date"
            className={classes.dateInput}
            value={reportStartDate}
            onChange={(e) => setReportStartDate(e.target.value)}
            title="Data inicial do relatório"
          />
          <input
            type="date"
            className={classes.dateInput}
            value={reportEndDate}
            onChange={(e) => setReportEndDate(e.target.value)}
            title="Data final do relatório"
          />
        </div>
        <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => handleOpenConfirmation('all')}>{t('ticketsManager.menu.all')}</MenuItem>
          <MenuItem onClick={() => handleOpenConfirmation('open')}>{t('ticketsManager.menu.open')}</MenuItem>
          <MenuItem onClick={() => handleOpenConfirmation('pending')}>{t('ticketsManager.menu.pending')}</MenuItem>
        </Menu>
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <FormControlLabel
              label={t('tickets.buttons.showAll')}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={showAllTickets}
                  onChange={() => setShowAllTickets((prev) => !prev)}
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
          )}
        />
        <TicketsQueueSelect
          style={{ marginLeft: 6 }}
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />
      </Paper>

      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <TicketsList
          status="open"
          showAll={showAllTickets}
          selectedQueueIds={selectedQueueIds}
          updateCount={(val) => setOpenCount(val)}
          style={applyPanelStyle('open')}
        />
      </TabPanel>
      <TabPanel value={tab} name="pending" className={classes.ticketsWrapper}>
        <TicketsList status="pending" showAll selectedQueueIds={selectedQueueIds} />
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList status="closed" showAll selectedQueueIds={selectedQueueIds} />
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList searchParam={searchParam} showAll selectedQueueIds={selectedQueueIds} />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;
